import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useSyncExternalStore } from 'react'
import PropTypes from 'prop-types'

import { backPropType } from './Screen.props'

const EMPTY = {}

const ScreenOptionsContext = createContext(null)

export const ScreenOptionsProvider = ScreenOptionsContext.Provider

/**
 * The bar's options, as the screen declared them and as its children have overridden them.
 *
 * @param {object} base - What the screen itself passed.
 * @returns {object} The options in force.
 */
export function useScreenOptions(base) {
  const store = useContext(ScreenOptionsContext)
  const overrides = useSyncExternalStore(store.subscribe, store.getSnapshot)

  return useMemo(() => ({ ...base, ...overrides }), [base, overrides])
}

/**
 * Creates the store a `Screen` provides. A store rather than state, and read only by the
 * bar, so a child changing an option re-renders the bar and nothing else. State here would
 * re-render the children that set it, which would set it again.
 *
 * @returns {object} The store.
 */
export function useScreenOptionsStore() {
  const entries = useRef(new Map())
  const listeners = useRef(new Set())
  const snapshot = useRef(EMPTY)

  const register = useCallback((id, options) => {
    if (options) entries.current.set(id, options)
    else entries.current.delete(id)

    const merged = {}
    for (const entry of entries.current.values()) {
      for (const [key, value] of Object.entries(entry)) {
        // An override that names one option leaves the others as the screen set them
        if (value !== undefined) merged[key] = value
      }
    }

    snapshot.current = Object.keys(merged).length ? merged : EMPTY
    listeners.current.forEach((listener) => listener())
  }, [])

  const subscribe = useCallback((listener) => {
    listeners.current.add(listener)
    return () => listeners.current.delete(listener)
  }, [])

  const getSnapshot = useCallback(() => snapshot.current, [])

  return useMemo(() => ({ register, subscribe, getSnapshot }), [register, subscribe, getSnapshot])
}

ScreenOptions.propTypes = {
  /** Replaces the bar's title */
  title: PropTypes.string,

  /** Replaces the bar's trailing controls */
  actions: PropTypes.node,

  /** Replaces how the way back behaves. See `backPropType`. */
  back: backPropType,
}

/**
 * Overrides part of the surrounding `Screen`'s bar for as long as this is mounted.
 *
 * A screen passes what it knows at the top, and anything deeper that learns something the
 * top could not says so from where it learned it: the wizard step that knows what back
 * means, the detail page that only has a title once its data arrives. Only the bar can be
 * overridden — the column width and the landmark are settled before any child renders.
 *
 * @param {object} props - The component props.
 * @returns {null} Renders nothing.
 */
export default function ScreenOptions({ title, actions, back }) {
  const store = useContext(ScreenOptionsContext)
  const id = useId()
  const latest = useRef(null)

  useEffect(() => {
    latest.current = back
  })

  // `back` is nearly always written inline, so its identity changes on every render. Taken
  // apart into the values that decide anything, with the handler called through a ref, it
  // re-registers only when something really moved.
  const declared = back !== undefined
  const hidden = back === false
  const { to, replace, onBack } = (!hidden && back) || {}
  const handled = Boolean(onBack)

  const resolved = useMemo(() => {
    if (!declared) return undefined
    if (hidden) return false
    return { to, replace, ...(handled && { onBack: () => latest.current?.onBack?.() }) }
  }, [declared, hidden, to, replace, handled])

  useEffect(() => {
    store.register(id, { title, actions, back: resolved })
    return () => store.register(id, null)
  }, [store, id, title, actions, resolved])

  return null
}
